import { Box, Checkbox, FormControl, ListItemText, MenuItem, Select, Typography, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState } from 'react';
import { brandColors } from 'types/colors';

const DropdownMenu = ({
  handleChange,
  selectedOption,
  options,
  labelName,
  float = 'right',
  inline = false,
  disabled,
  width,
  labelWidth,
  containerSx,
  labelControlSx,
  selectControlSx,
  selectSx,
  emptyLabel = 'Select',
  info = null,
  flexDirection = 'column',
  // Some fields, such as HVAC equipment, select more than one option.
  multiple = false,
  renderSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const baseSurfaceSx = {
    backgroundColor: '#ffffff',
    borderRadius: 2,
  };

  return (
    <Box
      mr={0.5}
      my={0.25}
      sx={{
        display: 'flex',
        flexDirection,
        flexWrap: 'nowrap',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: 0.75,
        width: '100%',
        ...containerSx,
      }}>
      {labelName && (
        <Box sx={{ display: 'flex', flex: '0 0 auto', alignItems: 'center', ...labelControlSx }}>
          <Typography mr={0.5} variant="body2" sx={{ width: labelWidth || 'fit-content', fontWeight: 600 }}>
            {labelName}
          </Typography>
          {info && (
            <Tooltip
              title={info}
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                  },
                },
              }}>
              <IconButton size="small" aria-label={`Smart info`} onClick={event => event.stopPropagation()}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <FormControl
        variant="standard"
        sx={{ m: 0.5, float: float, width: width || '100%', minWidth: 0, flex: '1 1 0', ...selectControlSx }}>
        <Select
          multiple={multiple}
          disabled={disabled}
          disableUnderline
          IconComponent={iconProps =>
            isOpen ? <KeyboardArrowUpIcon {...iconProps} /> : <KeyboardArrowDownIcon {...iconProps} />
          }
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: {
              sx: {
                ...baseSurfaceSx,
                borderRadius: '10px !important',
                mt: 0,
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
                border: '2px solid rgba(0, 0, 0, 0.08)',
              },
            },
          }}
          sx={{
            fontSize: 20,
            width: '100%',
            ...baseSurfaceSx,
            '&:before': { display: 'none' },
            '&:after': { display: 'none' },
            '&.Mui-focused:after': { display: 'none' },
            '& .MuiSelect-select': {
              py: 1,
              px: 1.5,
            },
            ...selectSx,
            ...(isOpen
              ? {
                  boxShadow: '0 0 0 2px rgba(66, 99, 235, 0.12)',
                }
              : {}),
          }}
          displayEmpty
          labelId={labelName}
          id={`id-${labelName}`}
          value={selectedOption ?? (multiple ? [] : '')}
          label={labelName}
          onChange={handleChange}
          fontSize="1rem"
          renderValue={val => {
            // A caller can choose a short summary instead of rendering every selected name.
            if (renderSelected) return renderSelected(val);
            if (val === '' || val == null) return <em>{emptyLabel}</em>;

            if (Array.isArray(val)) {
              if (val.length === 0) return <em>{emptyLabel}</em>;
              return options
                .filter(option => val.map(String).includes(String(option.value)))
                .map(option => option.name ?? option.label ?? String(option.value))
                .join(', ');
            }

            // find the selected option's display text
            const found = options.find(o => String(o.value) === String(val));
            const label = found ? (found.name ?? found.label ?? String(val)) : String(val);

            // italicize when label includes "Unassigned" or "Select"
            const shouldItalic = /unassigned|select/i.test(label);
            return shouldItalic ? <em>{label}</em> : label;
          }}>
          {options.map(o => (
            <MenuItem
              sx={{
                fontSize: 20,
                mx: 0.5,
                my: 0.25,
                px: 1,
                borderRadius: 1.5,
                // '&.Mui-selected': { backgroundColor: '#d2d4e6' },
                '&.Mui-selected': { backgroundColor: brandColors.BLUE, color: '#fff' },
                // '&.Mui-selected:hover': { backgroundColor: '#999eb9' },
                '&:hover': { backgroundColor: '#d2d4e6' },
              }}
              key={o.value + o.labelName}
              value={o.value}>
              {multiple ? (
                <>
                  {/* Keep checkboxes inside the shared menu so multi-select fields match normal dropdowns. */}
                  <Checkbox checked={(selectedOption || []).map(String).includes(String(o.value))} />
                  <ListItemText primary={o.name} />
                </>
              ) : (
                o.name
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default DropdownMenu;
